import { useEffect } from "react";
import { Shield, Lock, Eye, FileText, Globe, Mail } from "lucide-react";

function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-navy to-blue-900 py-16 sm:py-20 md:py-24 lg:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 p-4 rounded-full">
                <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Privacy Policy
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
              Your privacy is important to us. This policy outlines how we collect, use, and protect your personal information.
            </p>
            <p className="text-white/70 mt-4">Last Updated: October 28, 2025</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-text-dark text-base sm:text-lg leading-relaxed">
                At IMMACULATE (I.H.C.L.), we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services or visit our website.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <FileText className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    1. Information We Collect
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-4 text-text-dark">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Personal Information</h3>
                  <ul className="space-y-2 list-disc list-inside">
                    <li>Name, email address, phone number, and contact details</li>
                    <li>Employment history, qualifications, and professional credentials</li>
                    <li>Identification documents (passport, visa, Emirates ID)</li>
                    <li>Banking details for salary processing</li>
                    <li>Educational certificates and professional licenses</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Automatically Collected Information</h3>
                  <ul className="space-y-2 list-disc list-inside">
                    <li>IP address, browser type, and device information</li>
                    <li>Pages visited and time spent on our website</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Eye className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    2. How We Use Your Information
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>We use the information we collect to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Process employment applications and job placements</li>
                  <li>Manage recruitment and staffing operations</li>
                  <li>Communicate with candidates and clients</li>
                  <li>Process payroll and employment benefits</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Improve our services and website functionality</li>
                  <li>Send promotional materials and job opportunities (with your consent)</li>
                  <li>Conduct background checks and verification processes</li>
                </ul>
              </div>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Globe className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    3. Information Sharing and Disclosure
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>We may share your information with:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Client companies for employment purposes</li>
                  <li>Government agencies as required by Indian labor laws</li>
                  <li>Third-party service providers (payroll, background checks, IT services)</li>
                  <li>Professional advisors (lawyers, accountants, auditors)</li>
                  <li>Law enforcement when legally required</li>
                </ul>
                <p className="mt-4 font-semibold">
                  We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Lock className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    4. Data Security
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>
                  We implement appropriate technical and organizational measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Encrypted data transmission and storage</li>
                  <li>Secure servers and firewalls</li>
                  <li>Regular security audits and updates</li>
                  <li>Access controls and authentication protocols</li>
                  <li>Employee training on data protection</li>
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <div className="flex items-start mb-4">
                <Shield className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    5. Your Rights
                  </h2>
                </div>
              </div>
              <div className="pl-12 space-y-3 text-text-dark">
                <p>You have the right to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Access your personal information we hold</li>
                  <li>Request correction of inaccurate or incomplete data</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to processing of your personal data</li>
                  <li>Withdraw consent for marketing communications</li>
                  <li>Request data portability</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us at the details provided below.
                </p>
              </div>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                6. Data Retention
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, 
                  unless a longer retention period is required or permitted by law. Employment records are typically retained 
                  in accordance with Indian labor law requirements.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                7. Cookies and Tracking Technologies
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on our website. You can control 
                  cookie settings through your browser preferences. Note that disabling cookies may affect website functionality.
                </p>
              </div>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                8. Changes to This Privacy Policy
              </h2>
              <div className="text-text-dark space-y-3">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this policy 
                  periodically for any changes.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg border-l-4 border-gold">
              <div className="flex items-start mb-4">
                <Mail className="w-8 h-8 text-gold mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
                    Contact Us
                  </h2>
                </div>
              </div>
              <div className="text-text-dark space-y-2">
                <p>If you have any questions about this Privacy Policy, please contact us:</p>
                <p className="font-semibold mt-4">IMMACULATE (I.H.C.L.)</p>
                <p>Email: info@immaculate.ltd</p>
                <p>Phone: +91 99873 43532</p>
                <p>Address: Navi Mumbai 400703, Maharashtra, India</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicyPage;

